import axios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import FileSaver from 'file-saver';
import { useUserDetailsStore } from '../../store/userStore';

export const axiosInstance = axios.create({
  baseURL: '',
  headers: { 'Cache-Control': 'no-store, no-cache, must-revalidate, max-age=0', Expires: 0, Pragma: 'no-cache' },
  timeout: 120000, // 2 minutes
});

// The selected entity is a routing hint, validated against the session by client-advice.
// Attach it only to services that validate it; never to arbitrary download URLs.
axiosInstance.interceptors.request.use((request) => {
  if (request.url?.startsWith('/api/v1/client-advice/') || request.url?.startsWith('/api/v1/mps/')) {
    const entityId = useUserDetailsStore.getState().user?.accessibleEntityID;
    if (entityId) request.headers.set('X-Entity-ID', entityId);
  }
  return request;
});

function AuthRequestTransformer(config = {} as AxiosRequestConfig, isUnAuthRequest = false, noHeaders = false) {
  // Clone the config to avoid modifying the original object
  const newConfig = { ...config };
  const token = sessionStorage.getItem('accessToken');

  if (isUnAuthRequest) {
    // Define a new transformRequest function
    newConfig.transformRequest = [
      (data: any, headers: AxiosRequestHeaders) => {
        // Safely check and delete the Authorization header
        if (headers) {
          delete headers['Authorization'];
        }
        return data;
      },
      ...(Array.isArray(config.transformRequest) ? config.transformRequest : [config.transformRequest ?? ((data) => data)]),
    ];
  } else if (noHeaders) {
    // Define a new transformRequest function for noHeaders scenario
    newConfig.transformRequest = [
      (data: any, headers: AxiosRequestHeaders) => {
        if (headers) {
          headers['Cache-Control'] = 'private, max-age=86400';
          delete headers['Pragma'];
          delete headers['Expires'];
        }
        return data;
      },
      ...(Array.isArray(config.transformRequest) ? config.transformRequest : [config.transformRequest ?? ((data) => data)]),
    ];
  } else {
    newConfig.headers = {
      ...newConfig.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  // Return the modified copy of the config
  return newConfig;
}

/*
    NOTE: DO NOT ADD one off methods to this API.
    Use the axiosInstance or the config param of the respective API to extend your functionality.
 */
export const HttpClient = {
  get: <T>(url: string, config: AxiosRequestConfig<T> = axiosInstance.defaults as AxiosRequestConfig<T>, noAuth = false, noHeaders = false) => {
    return axiosInstance.get<T>(url, AuthRequestTransformer(config, noAuth, noHeaders) as AxiosRequestConfig<T>);
  },

  post: <T>(url: string, data?: any, config: AxiosRequestConfig<T> = axiosInstance.defaults as AxiosRequestConfig<T>, noAuth = false) => {
    return axiosInstance.post<T>(url, data, AuthRequestTransformer(config, noAuth) as AxiosRequestConfig<T>);
  },

  put: <T>(url: string, data?: any, config?: AxiosRequestConfig<T>, noAuth = false) => {
    return axiosInstance.put<T>(url, data, AuthRequestTransformer(config, noAuth) as AxiosRequestConfig<T>);
  },

  patch: <T>(url: string, data?: any, config?: AxiosRequestConfig<T>, noAuth = false) => {
    return axiosInstance.patch<T>(url, data, AuthRequestTransformer(config, noAuth) as AxiosRequestConfig<T>);
  },

  delete: <T>(url: string, config?: AxiosRequestConfig<T>, noAuth = false) => {
    return axiosInstance.delete<T>(url, AuthRequestTransformer(config, noAuth) as AxiosRequestConfig<T>);
  },

  upload: <T>(url: string, data?: any, config?: AxiosRequestConfig<T>, noAuth = false) => {
    return axiosInstance.postForm<T>(url, data, AuthRequestTransformer(config, noAuth) as AxiosRequestConfig<T>);
  },

  // NOTE: For GET pass config = { method: 'GET' }
  download: <T>(url: string, data: any, config?: AxiosRequestConfig<T>, noAuth = false, downloadDoc = true) => {
    return axiosInstance<T>(url, {
      method: 'POST',
      data: data,
      responseType: 'blob',
      ...AuthRequestTransformer(config, noAuth),
    })
      .then((response) => {
        if (response.status === 200) {
          // Improved regex to properly extract filename
          const contentDisposition = response.headers['content-disposition'];
          let filename = 'file';

          if (contentDisposition) {
            // Handle both quoted and unquoted filenames
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i;
            const matches = contentDisposition.match(filenameRegex);
            if (matches && matches[1]) {
              // Remove quotes if present
              filename = matches[1].replace(/['"]/g, '');
            }
          }

          if (downloadDoc) {
            FileSaver.saveAs(response.data as Blob, filename);
          }

          return response;
        }
        return response;
      })
      .catch(async (error) => {
        if (error.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const errorJson = text ? JSON.parse(text) : {};
            return Promise.reject({
              ...error,
              response: {
                ...error.response,
                data: errorJson,
              },
            });
          } catch {
            return Promise.reject(error);
          }
        }
        return Promise.reject(error);
      });
  },

  getDocument: <T>(url: string, config?: AxiosRequestConfig<T>, noAuth = false) => {
    return axiosInstance.get<T>(url, { responseType: 'blob', ...AuthRequestTransformer(config, noAuth) });
  },

  getBASE64Document: <T>(url: string, config?: AxiosRequestConfig<T>, noAuth = false) => {
    return axiosInstance.get<T>(url, { responseType: 'arraybuffer', responseEncoding: 'base64', ...AuthRequestTransformer(config, noAuth), ...config });
  },
};
